import { useEffect, useMemo, useState } from "react";
import {
  applyVariableTypes,
  effectiveFieldPresets,
  variableDataTypes,
  matchVariableType,
  previewVariableTypes,
  variableTypeMappingSchema,
  type FieldPreset,
  type FieldPresetCatalog,
  type FieldPresetScope,
  type Project,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Select, Button, Control, Toggle } from "./components";
import { fieldTypeName } from "./field-types";

export function VariableTypeMapper({
  project,
  onChange,
}: {
  project?: Project;
  onChange?: (project: Project) => void;
}) {
  const services = useAuthoringServices();
  const [catalog, setCatalog] = useState<FieldPresetCatalog>();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<FieldPreset>();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [aliases, setAliases] = useState("");
  const [dataType, setDataType] =
    useState<(typeof variableDataTypes)[number]>("text");
  const [match, setMatch] = useState<"exact" | "words">("words");
  const [enabled, setEnabled] = useState(true);
  const [scope, setScope] = useState<"application" | "site" | "company">(
    "company",
  );
  const [sample, setSample] = useState("shippingPhone");
  const [showPreview, setShowPreview] = useState(false);
  const load = async () => {
    try {
      if (!services.listFieldPresets)
        throw Error("Connect the workspace to manage variable mappings.");
      setCatalog(await services.listFieldPresets({ kind: "variable-type" }));
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, [services.listFieldPresets]);
  const rows = catalog?.presets || [];
  const effective = effectiveFieldPresets(rows);
  const changes = useMemo(
    () => (project ? previewVariableTypes(project, rows) : []),
    [project, catalog],
  );
  const context = catalog?.context;
  const canWriteScope = (kind: FieldPresetScope["kind"]) =>
    kind === "application"
      ? !!context?.canManageApplication
      : kind === "site"
        ? !!context?.canManageSite
        : kind === "company" && !!context?.canManageCompany;
  const start = (row?: FieldPreset, edit = false) => {
    setEditing(edit ? row : undefined);
    setKey(row?.key || "");
    setName(row?.name || "");
    const value = row ? variableTypeMappingSchema.parse(row.value) : undefined;
    setAliases(value?.aliases.join("\n") || "");
    setDataType(value?.dataType || "text");
    setMatch(value?.match || "words");
    setEnabled(value?.enabled ?? true);
    if (edit && row) setScope(row.scope.kind as typeof scope);
    setNotice("");
  };
  const save = async (archived = false) => {
    if (!context || !services.saveFieldPreset) return;
    setBusy(true);
    setNotice("");
    try {
      const value = variableTypeMappingSchema.parse({
        aliases: aliases
          .split(/\n|,/)
          .map((s) => s.trim())
          .filter(Boolean),
        dataType,
        match,
        enabled,
      });
      const target: FieldPresetScope =
        scope === "application"
          ? { kind: "application" }
          : scope === "site"
            ? { kind: "site", siteId: context.siteId }
            : {
                kind: "company",
                siteId: context.siteId,
                companyId: context.companyId,
              };
      const row = await services.saveFieldPreset({
        kind: "variable-type",
        key: key.trim(),
        name: name.trim(),
        value,
        scope: editing?.scope || target,
        expectedVersion: editing?.version || 0,
        ...(editing ? { id: editing.id } : {}),
        ...(archived ? { archived: true } : {}),
      });
      setEditing(row);
      await load();
      setShowPreview(false);
      setNotice(
        archived
          ? "Override removed. The inherited mapping is available again."
          : "Mapping saved. New imports use it automatically; existing projects keep their settings until applied.",
      );
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const result = matchVariableType({ key: sample, label: sample }, rows);
  return (
    <div className="proof-authoring">
      <section
        className="field-preset-library"
        aria-label="Variable data type mapper"
      >
        <h2>Variable data type mapper</h2>
        <p>
          Global → site → company. An override with the same mapping key
          replaces the broader mapping. Turn a mapping off to suppress it at
          that level. Removing an override restores inheritance.
        </p>
        <p>
          Names match complete words, including camelCase and underscores.
          Explicit variable types, calculations, choices and validation settings
          are preserved.
        </p>
        {error && <p role="alert">{error}</p>}
        <Control label="Try a variable name">
          <input value={sample} onChange={(e) => setSample(e.target.value)} />
        </Control>
        <p role="status">
          {result
            ? `${sample} → ${fieldTypeName(result.value.dataType)} · ${result.preset.scope.kind === "application" ? "global" : result.preset.scope.kind}`
            : "No matching mapping; keep the original type."}
        </p>
        <div className="control-grid">
          <section
            aria-label="Effective variable mappings"
            style={{ minWidth: 0, overflowX: "auto" }}
          >
            <h3>Effective mappings</h3>
            <table>
              <thead>
                <tr>
                  <th>Mapping</th>
                  <th>Names</th>
                  <th>Data type</th>
                  <th>Inherited from</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {effective.map((row) => {
                  const v = variableTypeMappingSchema.parse(row.value);
                  return (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        <small> · {row.key}</small>
                      </td>
                      <td>{v.aliases.join(", ")}</td>
                      <td>
                        {v.enabled ? fieldTypeName(v.dataType) : "Disabled"}
                      </td>
                      <td>
                        {row.scope.kind === "application"
                          ? "Global"
                          : row.scope.kind}
                      </td>
                      <td>
                        <Button onClick={() => start(row)}>Override</Button>
                        {!row.id.startsWith("builtin.") &&
                          canWriteScope(row.scope.kind) && (
                            <Button onClick={() => start(row, true)}>
                              Edit mapping
                            </Button>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows
              .filter(
                (row) =>
                  !row.archived &&
                  !row.id.startsWith("builtin.") &&
                  !effective.some((e) => e.id === row.id),
              )
              .map((row) => (
                <p key={row.id}>
                  {row.name} ·{" "}
                  {row.scope.kind === "application" ? "global" : row.scope.kind}{" "}
                  · overridden here{" "}
                  {canWriteScope(row.scope.kind) && (
                    <Button onClick={() => start(row, true)}>
                      Edit mapping
                    </Button>
                  )}
                </p>
              ))}
          </section>
          <section aria-label="Variable mapping editor">
            <h3>{editing ? "Edit mapping" : "Create mapping or override"}</h3>
            <Button onClick={() => start()}>New mapping</Button>
            <Control label="Mapping name">
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </Control>
            <Control
              label="Mapping key"
              note="Use an inherited key to override that mapping."
            >
              <input
                disabled={!!editing}
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            </Control>
            <Control label="Mapping scope">
              <Select
                value={scope}
                disabled={!!editing}
                onChange={(e) => setScope(e.target.value as typeof scope)}
              >
                <option
                  value="application"
                  disabled={!canWriteScope("application")}
                >
                  Global
                </option>
                <option value="site" disabled={!canWriteScope("site")}>
                  Site · {context?.siteName || "current site"}
                </option>
                <option value="company" disabled={!canWriteScope("company")}>
                  Company · {context?.companyName || "current company"}
                </option>
              </Select>
            </Control>
            <Control
              label="Variable names"
              note="One alias per line, or separate aliases with commas."
            >
              <textarea
                value={aliases}
                onChange={(e) => setAliases(e.target.value)}
              />
            </Control>
            <Control label="Match names">
              <Select
                value={match}
                onChange={(e) => setMatch(e.target.value as typeof match)}
              >
                <option value="words">Complete words within the name</option>
                <option value="exact">Exact name</option>
              </Select>
            </Control>
            <Control label="Mapped data type">
              <Select
                value={dataType}
                onChange={(e) => setDataType(e.target.value as typeof dataType)}
              >
                {variableDataTypes.map((t) => (
                  <option key={t} value={t}>
                    {fieldTypeName(t)}
                  </option>
                ))}
              </Select>
            </Control>
            <Toggle
              value={enabled}
              onChange={setEnabled}
              label="Enable mapping"
            />
            <div className="button-row">
              <Button
                disabled={
                  busy ||
                  !!error ||
                  !canWriteScope(scope) ||
                  !name.trim() ||
                  !key.trim() ||
                  !aliases.trim()
                }
                onClick={() => void save()}
              >
                Save mapping
              </Button>
              {editing && !editing.archived && (
                <Button
                  disabled={busy || !canWriteScope(scope)}
                  onClick={() => void save(true)}
                >
                  Remove override
                </Button>
              )}
            </div>
            {notice && <p role="status">{notice}</p>}
          </section>
        </div>
        {project && onChange && (
          <section aria-label="Apply variable mappings">
            <h3>Apply to {project.name}</h3>
            <p>
              Preview changes before updating this project. Published versions
              remain unchanged.
            </p>
            <Button
              disabled={!!error || !catalog || !context?.canManageProject}
              onClick={() => setShowPreview(true)}
            >
              Preview project changes
            </Button>
            {showPreview && (
              <>
                <ul>
                  {changes.map((c) => (
                    <li key={c.fieldId}>
                      {c.label}: {fieldTypeName(c.from)} → {fieldTypeName(c.to)}{" "}
                      · {c.mapping} (
                      {c.scope === "application" ? "global" : c.scope})
                    </li>
                  ))}
                </ul>
                {!changes.length && (
                  <p>No eligible variables need a type change.</p>
                )}
                <Button
                  disabled={!changes.length || !context?.canManageProject}
                  onClick={() => {
                    onChange(applyVariableTypes(project, rows));
                    setShowPreview(false);
                    setNotice(
                      `Applied ${changes.length} variable type changes. Save the project to keep them.`,
                    );
                  }}
                >
                  Apply {changes.length} changes
                </Button>
              </>
            )}
          </section>
        )}
      </section>
    </div>
  );
}
